import type { RefObject } from 'react';
import { useCallback, useRef } from 'react';

import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { AutoScroller, Cursor, Feedback, PointerSensor, PreventSelection } from '@dnd-kit/dom';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { DragIndicator } from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Tooltip } from '@mui/material';

import ClipboardListOutline from '@/assets/icons/ClipboardListOutline';
import Button from '@/components/Buttons/Button';
import type { SymbolKey } from '@/entities/symbol';
import AccordionSection from '@/features/DataHub/components/AccordionSection';
import {
  DATAHUB_ITEM_DETAILS_MAX_ROWS,
  DATAHUB_LIST_ROW_HEIGHT,
  DATAHUB_ROW_HEIGHT
} from '@/features/DataHub/constants/ui';
import type { CatalogSymbolItem } from '@/features/DataHub/hooks/useCatalogSymbolItems';
import { useCatalogSymbolItems } from '@/features/DataHub/hooks/useCatalogSymbolItems';
import { useSelectedSymbolsActions } from '@/features/DataHub/hooks/useSelectedSymbolsActions';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';

const COLUMN_WIDTHS = {
  alias: '80px',
  code: '90px',
  currency: '75px',
  units: '60px'
};

const sensors = [
  PointerSensor.configure({
    activatorElements(source) {
      return [source.element, source.handle];
    }
  })
];

const ItemsHeader = ({ readOnly }: { readOnly?: boolean }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        fontSize: '12px',
        gap: '16px'
      }}
    >
      {!readOnly && (
        <Box
          sx={{
            width: '24px'
          }}
        />
      )}
      <Box sx={{ width: COLUMN_WIDTHS.alias }}>Alias</Box>
      <Box sx={{ width: COLUMN_WIDTHS.code }}>GX Code</Box>
      <Box sx={{ width: COLUMN_WIDTHS.currency }}>Currency</Box>
      <Box sx={{ width: COLUMN_WIDTHS.units }}>Unit</Box>
      <Box sx={{ flex: 1 }}>Short Title</Box>
    </Box>
  );
};

export const ItemRow = ({
  item,
  readOnly,
  index,
  containerRef
}: {
  item: CatalogSymbolItem;
  readOnly?: boolean;
  index: number;
  containerRef: RefObject<HTMLDivElement | null>;
}) => {
  const { removeSymbolFromSelection } = useSelectionFunctions();
  const handleClick = useCallback(() => {
    removeSymbolFromSelection(item.symbol);
  }, [item.symbol, removeSymbolFromSelection]);

  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLElement | null>(null);

  useSortable({
    id: item.id,
    index,
    element: ref,
    handle: handleRef,
    type: 'item',
    accept: 'item',
    disabled: readOnly,
    modifiers: [
      RestrictToVerticalAxis,
      RestrictToElement.configure({ element: containerRef.current })
    ]
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1.5,
        height: DATAHUB_ROW_HEIGHT,
        minHeight: DATAHUB_ROW_HEIGHT,
        borderWidth: 1,
        borderColor: 'text.primary',
        borderStyle: 'solid',
        flex: 1,
        fontSize: '14px',
        gap: 2
      }}
    >
      {!readOnly && (
        <Box
          ref={handleRef}
          sx={{
            width: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            flexShrink: 0
          }}
        >
          <DragIndicator sx={{ fontSize: 24 }} />
        </Box>
      )}
      <Box sx={{ width: COLUMN_WIDTHS.alias }}>{item.alias}</Box>
      <Box sx={{ width: COLUMN_WIDTHS.code }}>{item.code}</Box>
      <Box sx={{ width: COLUMN_WIDTHS.currency }}>{item.currency}</Box>
      <Box sx={{ width: COLUMN_WIDTHS.units }}>{item.units}</Box>
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        <Tooltip title={item.title}>
          <Box component='span'>{item.title}</Box>
        </Tooltip>
      </Box>
      {!readOnly && (
        <Button
          sx={{
            width: '32px',
            height: '32px',
            backgroundColor: 'gx.actionBarButtonTile',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleClick}
        >
          <DeleteOutlineIcon
            sx={{
              mr: '0 !important'
            }}
          />
        </Button>
      )}
    </Box>
  );
};

export const ItemDetails = ({
  symbols,
  readOnly
}: {
  symbols: SymbolKey[];
  readOnly?: boolean;
}) => {
  const { items } = useCatalogSymbolItems(symbols);
  const { reorderSelectedSymbols } = useSelectedSymbolsActions();
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <AccordionSection
      icon={<ClipboardListOutline />}
      title='Item details'
    >
      <ItemsHeader readOnly={readOnly} />
      <Box
        ref={containerRef}
        sx={{
          maxHeight: DATAHUB_ITEM_DETAILS_MAX_ROWS * DATAHUB_LIST_ROW_HEIGHT,
          overflow: 'auto'
        }}
      >
        <DragDropProvider
          plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
          sensors={sensors}
          onDragEnd={event => {
            const reordered = move(items, event);
            const newSymbols = reordered.map(item => item.symbol);
            reorderSelectedSymbols(newSymbols);
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {items.map((item, index) => (
              <ItemRow
                key={item.id}
                containerRef={containerRef}
                index={index}
                item={item}
                readOnly={readOnly}
              />
            ))}
          </Box>
        </DragDropProvider>
      </Box>
    </AccordionSection>
  );
};
